/**
 * Analyse du vent : quelle altitude porte le panache, et depuis quand le vent
 * souffle dans sa direction actuelle.
 *
 * C'est le raisonnement le moins intuitif de l'application, et le plus
 * important. Il est isolé ici pour pouvoir être testé seul.
 */
import { WIND_SHIFT_CONE_DEG } from './constants';
import { angleDeltaDeg } from './geo';
import type {
  WeatherSnapshot,
  WindLevel,
  WindLevelKey,
  WindRegime,
  WindRegimes,
  WindSample,
} from './types';

/**
 * Trois niveaux, parce qu'un panache ne voyage pas à la hauteur des haies.
 *
 * Plus un feu est vaste, plus il est chaud, plus sa fumée s'élève, et plus
 * haut elle monte, plus le vent qui l'emporte est rapide. Le rayon de la
 * zone détectée sert donc d'indicateur de puissance thermique.
 *
 * Relevé pendant les feux de Gironde : 7 km/h au sol, 11 km/h à 180 m, mais
 * 44 km/h vers 1 500 m, avec 50° d'écart de direction. Utiliser le vent
 * mesuré à 10 m donnait des durées de trajet trois à cinq fois trop longues,
 * et désignait les mauvais foyers.
 */
export const WIND_LEVELS: readonly WindLevel[] = [
  { key: '180m', label: '180 m', maxRadiusKm: 3 },
  { key: '850hPa', label: '1 500 m', maxRadiusKm: 8 },
  { key: '700hPa', label: '3 000 m', maxRadiusKm: Infinity },
];

const DEFAULT_LEVEL = WIND_LEVELS[0]!;
const HIGHEST_LEVEL = WIND_LEVELS[WIND_LEVELS.length - 1]!;

/** Le niveau qui porte la fumée d'une zone, d'après son étendue. */
export function levelForRadius(radiusKm: number): WindLevel {
  return WIND_LEVELS.find((level) => radiusKm < level.maxRadiusKm) ?? HIGHEST_LEVEL;
}

export function levelByKey(key: WindLevelKey): WindLevel {
  return WIND_LEVELS.find((level) => level.key === key) ?? DEFAULT_LEVEL;
}

/**
 * Depuis quand le vent souffle-t-il dans sa direction actuelle ?
 *
 * On remonte la série horaire tant que chaque point reste dans un cône de
 * ±40° autour de la direction *actuelle*. Le premier point qui en sort
 * marque la bascule.
 *
 * Ce moment est le seul point de départ valable pour parler d'arrivée de
 * fumée. Si le vent souffle de la même direction depuis des heures, le
 * panache est en régime établi : la fumée est déjà arrivée, ou déjà passée,
 * et annoncer une heure d'arrivée n'aurait aucun sens.
 *
 * Le calcul est fait séparément pour chaque altitude : le vent de 3 000 m
 * peut avoir tourné à 9 h et celui de 180 m à 14 h.
 */
export function windRegime(series: readonly WindSample[] | undefined): WindRegime | null {
  if (!series || series.length === 0) return null;

  const last = series[series.length - 1]!;

  let index = series.length - 1;
  while (index > 0) {
    const previous = series[index - 1]!;
    if (angleDeltaDeg(previous.direction, last.direction) > WIND_SHIFT_CONE_DEG) break;
    index -= 1;
  }

  const start = series[index]!;
  const turned = index > 0;

  return {
    direction: last.direction,
    speed: last.speed,
    at: last.at,
    steadySince: start.at,
    turned,
    previousDirection: turned ? series[index - 1]!.direction : null,
  };
}

/**
 * Les trois régimes d'un coup.
 *
 * À appeler une seule fois par cycle de rendu, puis à passer aux fonctions
 * qui en ont besoin. Les recalculer par zone reviendrait à reparcourir
 * l'historique des centaines de fois pour un résultat identique.
 */
export function allRegimes(weather: WeatherSnapshot | null): WindRegimes {
  if (!weather) return {};
  const out: Partial<Record<WindLevelKey, WindRegime | null>> = {};
  for (const level of WIND_LEVELS) {
    out[level.key] = windRegime(weather.series[level.key]);
  }
  return out;
}

/**
 * Le régime du niveau demandé, avec repli sur celui de 180 m.
 *
 * Open-Meteo peut ne pas fournir un niveau de pression donné pour un point ;
 * mieux vaut alors raisonner sur le niveau le plus bas que ne rien dire.
 */
export function regimeFor(regimes: WindRegimes, key: WindLevelKey): WindRegime | null {
  return regimes[key] ?? regimes['180m'] ?? null;
}
